/**
 * What an upload that did not reach the gallery should say in the activity log, and how loud.
 *
 * Every unregistered file used to log the same error, "not registered in gallery yet, handed to
 * the retry queue". Two very different things hid behind it (activity logs, 2026-09-29..10-06,
 * 274 sessions on "Individual photos upload incomplete"):
 *
 * - A refusal. /api/gallery's upload gate answers 403 `NO_ENTITLEMENT` (`terminal: true`) when
 *   the booth sends no licence bound to its device and the owner has no credits. 184 of the 274
 *   were one booth that had lost its activation code: every session was credit-blocked, and the
 *   line never said so. That is worth an error, with the server's code in it.
 * - A transient failure (network, 5xx). The retry queue normally lands the file within minutes,
 *   so logging it as an error made a routine retry look like a lost photo. That is a warning.
 *
 * Pure (the type import is erased) so it tests without the renderer.
 */
import type { UploadFailure, UploadResult } from '@renderer/types/outputPipeline.types'

/** A non-2xx answer from POST /api/gallery. */
export interface GalleryRefusal {
  status: number
  /** The upload gate's machine-readable code, when it sent one. */
  code?: string
  /** True only when the server marked the code terminal: retrying cannot change the answer. */
  terminal: boolean
  message: string
}

/**
 * Read a non-2xx /api/gallery body. The upload gate sends `{ error, code, terminal }`; anything
 * else (a proxy page, a bare "Forbidden") is kept as text and is never terminal.
 *
 * The session-id collision guard sends `{ error: "session_id_conflict", message, suggestedId }`
 * with a 409 and no code: the id already holds another account's gallery and never frees up, so
 * it is read as the terminal code SESSION_ID_CONFLICT.
 */
export function parseGalleryRefusal(status: number, bodyText: string): GalleryRefusal {
  let body: Record<string, unknown> = {}
  try {
    const parsed: unknown = JSON.parse(bodyText)
    if (parsed && typeof parsed === 'object') body = parsed as Record<string, unknown>
  } catch {
    // not JSON: keep the text
  }
  if (status === 409 && body.error === 'session_id_conflict') {
    return {
      status,
      code: 'SESSION_ID_CONFLICT',
      terminal: true,
      message: typeof body.message === 'string' ? body.message : 'session_id_conflict'
    }
  }
  const code = typeof body.code === 'string' && body.code ? body.code : undefined
  const message = typeof body.error === 'string' ? body.error : bodyText.slice(0, 200)
  return { status, code, terminal: body.terminal === true && code !== undefined, message }
}

type Refused = Extract<UploadFailure, { kind: 'refused' }>

export interface UnregisteredOutcome {
  level: 'warn' | 'error'
  /** Finishes "... the rest <reason>" and "Strip upload <reason>: <file>". */
  reason: string
  /** The server's refusal code, when it refused. */
  code?: string
}

/**
 * Level and reason for files that uploadFile returned without `uploadedUrl`. Pass only those
 * results. A refusal anywhere in the batch wins: one refused file means the booth is refused.
 */
export function unregisteredOutcome(
  unregistered: readonly UploadResult[],
  signedIn: boolean
): UnregisteredOutcome {
  if (!signedIn) return { level: 'error', reason: 'skipped: no signed-in user on this booth' }
  const refused = unregistered
    .map((r) => r.failure)
    .find((f): f is Refused => f?.kind === 'refused')
  if (refused) {
    return {
      level: 'error',
      reason: `refused by the server (${refused.code}: ${refused.message}), handed to the retry queue`,
      code: refused.code
    }
  }
  return { level: 'warn', reason: 'not registered in gallery yet, handed to the retry queue' }
}
